import { useContext, useEffect, useMemo, useState } from "react";

import RightArrow from "../../../../../../../images/ArrowRight.svg";
import { TableType } from "../../../../../customer/companies/queries/types";
import { GetTickets } from "../../../../../../../queries/queries";
import {
  AtsTicketsOrderField,
  GetTicketsVars,
  TicketsQueryType,
} from "../../../../../../../queries/types";
import { GetTicketsResult, TicketEdge } from "../../../queries/types";
import { useLazyQuery } from "@apollo/client";
import {
  convertToTimeZone,
  getModifiedScrollHeight,
  ticketStatusClassName,
  toStandardDate,
} from "../../../../../../../utils/UtilFunctions";
import {
  EMPTY_STRING,
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
  TicketStatus,
} from "../../../../../../../utils/constants";

import { MapsContext } from "../../../context/context";
import useUrlParams from "../../../../../../../customHooks/useUrlParams";
import { OrderDirection } from "../../../../../../../utils/commonQueryTypes";
import useLoggedInUserDetails from "../../../../../../../customHooks/useLoggedInUserDetails";
import { TimeZone } from "../../../../../../../utils/Enum.types";
import { mapsPayloadType } from "../../../context/reducer";
import useServerDateandTime from "../../../../../../../customHooks/useServerDateandTime";
import { AssetDetailsPageFor } from "./AssetDetails";
import ItemDetails from "../ItemDetails";

interface Props {
  pageFor: AssetDetailsPageFor;
  assetType: TableType;
}
const TicketItemDetails = ({ pageFor, assetType }: Props) => {
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [tickets, setTickets] = useState<TicketEdge[]>([]);

  const { state, dispatch } = useContext(MapsContext);
  const { customer_id } = useUrlParams();
  const { user_details } = useLoggedInUserDetails();
  const {
    serverDate,
    data: serverData,
    loading: serverLoading,
  } = useServerDateandTime();

  const [GetT, { data, loading, fetchMore }] = useLazyQuery<
    GetTicketsResult,
    GetTicketsVars
  >(GetTickets, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetTickets.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCustomer) => {
          const filteredStudent = tickets.find(
            (ticket) => ticket.node.id === newCustomer.node.id
          );
          if (filteredStudent) {
            return {
              ...newCustomer,
              node: {
                ...newCustomer.node,
              },
            };
          }
          return newCustomer;
        });

        setTickets(updatedNewData);
      } else {
        setTickets(newData);
      }

      setEndCursor(data.GetTickets.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [loading, data]);

  const rows = useMemo(() => {
    return data
      ? data.GetTickets.edges.map(({ node }, index) => ({
          id: index + 1,
          logNo: node.ticketNo,
          asset: node.item.desc,
          asset_slno: node.itemDetails.prodSerialNo,
          ticket_date: toStandardDate(node.ticketDate),
          subsidiary: node.subsidiaryDetails.name,
          ticket_reason: EMPTY_STRING,
          status: node.status,
          maintained_by: "",
          attachments: "",
          priority: node.priority,
          ticket_status: node.status,
          ticket_id: node.id,
          asset_id: node.itemDetails.id,
        }))
      : [];
  }, [data]);

  useEffect(
    () => {
      const scrollTable = document.getElementsByClassName(
        "MuiDataGrid-virtualScroller"
      )[0] as Element;
      const handleScroll = (e: Event) => {
        const target = e.target as HTMLDivElement;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
          if (hasNextPage && !loading) {
            fetchMore({
              variables: {
                first: FETCH_MORE_DATA,
                after: endCursor,
              },
              updateQuery: (prevResult, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prevResult;

                const newEdges = fetchMoreResult.GetTickets.edges;
                const pageInfo = fetchMoreResult.GetTickets.pageInfo;

                setEndCursor(pageInfo.endCursor);
                setHasNextPage(pageInfo.hasNextPage);

                const duplicateCheck = prevResult.GetTickets
                  ? prevResult.GetTickets.edges.filter(
                      ({ node: { id } }) =>
                        newEdges.findIndex(
                          ({ node: { id: newId } }) => newId === id
                        ) !== -1
                    )
                  : [];

                if (duplicateCheck.length > 0) return prevResult;

                return {
                  GetTickets: {
                    edges: [...tickets, ...newEdges],
                    pageInfo,
                    totalCount: data ? data.GetTickets.totalCount! : 0,
                  },
                };
              },
            });
          }
        }
      };
      if (scrollTable && rows.length)
        scrollTable.addEventListener("scroll", handleScroll);

      return () => {
        // eslint-disable-next-line
        if (scrollTable)
          scrollTable.removeEventListener("scroll", handleScroll);
      };
    },
    // eslint-disable-next-line
    [rows, endCursor]
  );

  useEffect(() => {
    if (customer_id) {
      GetT({
        variables: {
          after: null,
          first: ROWS_PER_PAGE,
          name: "",
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsTicketsOrderField.TICKET_NO,
          },

          companyID: 0,
          customerID: customer_id,
          subsidiaryID: 0,
          junctionID: 0,
          reason: EMPTY_STRING,
          input: {
            itemID: 0,
            ldgrID: 0,
            stckCategoryID: 0,
            stckGroupID: 0,
            stckManufacturerID: 0,
            stckSubCategoryID: 0,
            stckUomID: 0,
            query_type: TicketsQueryType.ALL,
            itemDetailsID: state.itemDetailsId,
            status: [
              TicketStatus.ASSIGNED,
              TicketStatus.OPEN,
              TicketStatus.RESOLVED,
              TicketStatus.RE_OPENED,
            ],
            empID: user_details.id!,
            priority: [],
          },
          siteID: 0,
        },
      });
    }
  }, [
    GetT,
    state.itemDetailsId,
    user_details.id,
    customer_id,
    assetType,
    data,
  ]);

  const getAging = (date: string) => {
    if (serverData && !serverLoading) {
      const presentDate = new Date(serverDate);
      const ticketDate = new Date(date);

      const diffMs = Math.abs(ticketDate.getTime() - presentDate.getTime()); // Absolute difference in ms

      const diffSec = Math.floor(diffMs / 1000); // Total seconds
      const diffMin = Math.floor(diffSec / 60); // Total minutes
      const diffHours = Math.floor(diffMin / 60); // Total hours
      const diffDays = Math.floor(diffHours / 24); // Total days

      const remainingHours = diffHours % 24; // Hours excluding full days
      const remainingMinutes = diffMin % 60; // Minutes excluding full hours
      const remainingSeconds = diffSec % 60; // Seconds excluding full minutes

      let result = "";
      if (diffDays) result += `${diffDays} days `;
      if (remainingHours) result += `${remainingHours} hrs `;
      if (remainingMinutes) result += `${remainingMinutes} mins `;
      if (remainingSeconds) result += `${remainingSeconds} secs`;

      return result.trim(); // Remove extra spaces
    }
    return "";
  };

  return (
    <div className="jucn-ticket-details">
      <ItemDetails assetType={TableType.ASSET} viewIcon={true} />
      <div className="jucn-ticket-details__header">
        <b>Ticket Raised</b>
      </div>
      <ul className="jucn-ticket-details__ul">
        {pageFor === AssetDetailsPageFor.MAPS ? (
          <>
            {data
              ? data.GetTickets.edges.map(({ node }, index) => {
                  return (
                    <li
                      onClick={() => {
                        dispatch({
                          type: mapsPayloadType.TICKET,
                          payload: {
                            ticketId: node.id
                          },
                        });
                        dispatch({
                          type: mapsPayloadType.COMPANY_ID,
                          payload: {
                            companyId: node.company.id,
                          },
                        });
                      }}
                    >
                      <span>{index + 1}.</span>
                      <div>
                        <div className="jucn-ticket-details__ul--grid">
                          <label>Ticket No.</label>
                          <span>:</span>
                          <b className="font-red">{node.ticketNo}</b>
                        </div>
                        <div className="jucn-ticket-details__ul--grid">
                          <label>Ticket Raised Date</label>
                          <span>:</span>
                          <b>
                            {
                              convertToTimeZone(
                                TimeZone.Asia_Kolkata,
                                node.ticketDate
                              ).date
                            }{" "}
                            <span className="font-grey">
                              {
                                convertToTimeZone(
                                  TimeZone.Asia_Kolkata,
                                  node.ticketDate
                                ).time
                              }
                            </span>
                          </b>
                        </div>
                        <div className="jucn-ticket-details__ul--grid">
                          <label>Ticket Reason</label>
                          <span>:</span>
                          <b>{node.reason}</b>
                        </div>
                        <div className="jucn-ticket-details__ul--grid">
                          <label>Ageing</label>
                          <span>:</span>
                          <b>{getAging(node.ticketDate)} </b>
                        </div>
                        <div className="jucn-ticket-details__ul--grid">
                          <label>Maintained By</label>
                          <span>:</span>
                          <b>{node.itemDetails.vendorDetails.desc}</b>
                        </div>
                        <div className="jucn-ticket-details__ul--grid">
                          <label>Ticket Status</label>
                          <span>:</span>
                          <b
                            className={`jucn-ticket-details__ul--status ${ticketStatusClassName(
                              node.status
                            )}`}
                          >
                            {node.status}
                          </b>
                        </div>
                      </div>
                      <img src={RightArrow} alt="" />
                    </li>
                  );
                })
              : null}
          </>
        ) : null}
      </ul>
    </div>
  );
};

export default TicketItemDetails;
